/**
 * What a limited ServiceTitan user needs, from Volca's two permission sheets.
 *
 * One list, not two. A business running payroll and accounts payable needs the union of
 * the sheets, and roughly half of each sheet is the same permission: a combined customer
 * reading two lists would tick "View job" twice and wonder which one counted. So every
 * permission is named once and carries the products that want it, and the guide shows
 * the ones its own business needs.
 *
 * Keep the labels exactly as ServiceTitan writes them. The customer is matching this
 * text against a list of checkboxes in somebody else's settings screen, so a tidied-up
 * wording costs them a search.
 */
export const PERMISSION_PRODUCT = {
  payroll: "payroll",
  accountsPayable: "accounts_payable",
} as const;

export type PermissionProduct =
  (typeof PERMISSION_PRODUCT)[keyof typeof PERMISSION_PRODUCT];

export interface PermissionItem {
  label: string;
  products: readonly PermissionProduct[];
  /**
   * ServiceTitan hides some purchasing permissions until that feature is on for the
   * account. Marked so the guide can say so once, rather than leaving a customer
   * hunting for a checkbox that is not there.
   */
  hidden?: true;
}

export interface PermissionGroup {
  title: string;
  items: readonly PermissionItem[];
}

const BOTH = [
  PERMISSION_PRODUCT.payroll,
  PERMISSION_PRODUCT.accountsPayable,
] as const;
const PAYROLL = [PERMISSION_PRODUCT.payroll] as const;
const AP = [PERMISSION_PRODUCT.accountsPayable] as const;

export const PERMISSION_GROUPS: readonly PermissionGroup[] = [
  {
    title: "General",
    items: [
      { label: "Access settings", products: BOTH },
      { label: "Generate API Application Key", products: BOTH },
      { label: "Manage API Application Access", products: BOTH },
    ],
  },
  {
    title: "Invoices",
    items: [
      { label: "View invoice", products: BOTH },
      { label: "Edit invoice", products: PAYROLL },
      { label: "Edit invoice item", products: PAYROLL },
      { label: "View invoice numbers", products: BOTH },
      { label: "Show cost details", products: BOTH },
    ],
  },
  {
    title: "Costing",
    items: [{ label: "View labor costing details", products: BOTH }],
  },
  { title: "Customers", items: [{ label: "View customer", products: BOTH }] },
  {
    title: "Dispatching",
    items: [{ label: "View dispatch board", products: PAYROLL }],
  },
  { title: "Estimates", items: [{ label: "View estimate", products: BOTH }] },
  {
    title: "Jobs",
    items: [
      { label: "View job", products: BOTH },
      { label: "Edit job", products: BOTH },
    ],
  },
  { title: "Locations", items: [{ label: "View location", products: BOTH }] },
  {
    title: "Projects",
    items: [{ label: "View Project Portfolio", products: BOTH }],
  },
  {
    title: "Business Units",
    items: [{ label: "View business units", products: BOTH }],
  },
  {
    title: "Subcontractors",
    items: [{ label: "View subcontractors", products: AP }],
  },
  { title: "Search", items: [{ label: "Run search", products: BOTH }] },
  {
    title: "Dashboard",
    items: [{ label: "View dashboard", products: PAYROLL }],
  },
  {
    title: "Employees and Technicians",
    items: [
      {
        label: "View or update payroll tab on technician edit",
        products: PAYROLL,
      },
    ],
  },
  {
    title: "Payroll, general",
    items: [
      { label: "Can access global payroll settings", products: PAYROLL },
      { label: "View and edit labor types", products: PAYROLL },
      { label: "View and edit overtime module", products: PAYROLL },
      { label: "View and edit paid idle time rule", products: PAYROLL },
      { label: "View and edit configurable payroll module", products: PAYROLL },
    ],
  },
  {
    title: "Timesheets",
    items: [
      { label: "View employee time tracking and edit page", products: PAYROLL },
      {
        label: "View and edit all office employee timesheets",
        products: PAYROLL,
      },
      { label: "View technician timesheets", products: PAYROLL },
      {
        label: "View hourly pay on timesheet summary report",
        products: PAYROLL,
      },
    ],
  },
  {
    title: "Payroll Adjustment",
    items: [{ label: "View payroll adjustment", products: PAYROLL }],
  },
  {
    title: "Payroll Admin",
    items: [
      { label: "View payroll approval dashboard", products: PAYROLL },
      { label: "View and edit payroll codes", products: PAYROLL },
    ],
  },
  {
    title: "Reports",
    items: [{ label: "View job costing", products: PAYROLL }],
  },
  {
    title: "Pricebook",
    items: [
      { label: "View pricebook", products: BOTH },
      { label: "View pricing rates", products: PAYROLL },
      { label: "Edit service", products: AP },
      { label: "Edit material", products: AP },
      { label: "Edit equipment", products: AP },
      { label: "Edit category", products: AP },
      { label: "Edit discounts and fees", products: AP },
      { label: "Template Pricebook Items", products: AP },
      { label: "View Product Types", products: AP },
      { label: "Edit Product Types", products: AP },
    ],
  },
  {
    title: "Purchase Orders",
    items: [
      { label: "View Purchase Orders", products: BOTH },
      {
        label: "View Purchase Order Totals on Projects and Invoices",
        products: BOTH,
      },
      { label: "Create purchase order", products: AP, hidden: true },
      { label: "Edit purchase order in pending status", products: AP },
      {
        label: "Edit purchase order in sent or partially received status",
        products: AP,
      },
      { label: "Cancel purchase order", products: AP },
      { label: "Add/Edit Purchase Order types", products: AP },
      { label: "Search Vendor Catalog Items In Purchase Order", products: AP },
      {
        label: "Add Vendor Catalog Items To Pricebook From Purchase Order",
        products: AP,
      },
    ],
  },
  {
    title: "Receipts",
    items: [{ label: "Edit receipt", products: AP, hidden: true }],
  },
  {
    title: "Returns",
    items: [
      { label: "View Returns", products: BOTH },
      {
        label: "View Return Totals on Projects and Invoices",
        products: AP,
      },
      { label: "Create return", products: AP, hidden: true },
      { label: "Cancel return", products: AP, hidden: true },
    ],
  },
  {
    title: "Payables",
    items: [
      { label: "View bills & statements", products: AP },
      { label: "Edit bill", products: AP },
      { label: "Cancel bill", products: AP },
    ],
  },
  {
    title: "Adjustments",
    items: [{ label: "View Adjustment $ Value", products: PAYROLL }],
  },
  {
    title: "Inventory Settings",
    items: [{ label: "Access Purchasing/Inventory Module", products: AP }],
  },
];

/** The groups a business actually needs, with empty ones dropped. */
export const permissionGroupsFor = (
  products: readonly PermissionProduct[],
): PermissionGroup[] =>
  PERMISSION_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) =>
      item.products.some((product) => products.includes(product)),
    ),
  })).filter((group) => group.items.length > 0);
